import { useState } from 'react';
import { ContentType } from 'types/publishedAssets';
import { ContentContainer } from 'components/Content/Atoms/ContentContainer';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { BlockHeading } from 'components/Content/Atoms/BlockHeading';
import { Icon } from 'components/Atoms/Icon/Icon';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Spacer } from 'components/Atoms/Spacer/Spacer';
import { getInitialVisibleCount } from 'src/utils/getInitialVisibleCount';
import { ProgressComplete } from './atoms/ProgressComplete';
import { ProgressCurrent } from './atoms/ProgressCurrent';
import { ProgressFuture } from './atoms/ProgressFuture';
import { getProgress, Progress } from './helpers';
import styles from './Timeline.module.scss';

interface Props {
  content: ContentType;
}

export function Timeline(props: Props) {
  const { content } = props;
  const { translate } = useGetContentTranslations();
  const { t } = useTranslation();
  const showByDefault = getInitialVisibleCount(content.metadata, 3);
  const [showAllPhases, setShowAllPhases] = useState(false);
  const title = translate(content.translations, 'label');
  const secondaryText = translate(content.translations, 'description');
  const phaseCount = content.components.length;
  return (
    <ContentContainer content={content}>
      <div className={styles.timelineContentContainer}>
        <BlockHeading title={title} secondaryText={secondaryText} id={content.id} />
        <div className={styles.timelineContainer}>
          {content.components.map((component, index) => {
            if (showByDefault && !showAllPhases && index > showByDefault - 1) return null;
            const startDate = component?.metadata?.start_date;
            const endDate = component?.metadata?.end_date;
            const entryTitle = translate(component.translations!, 'title');
            const description = translate(component.translations!, 'description');
            const progress = getProgress(startDate, endDate);
            const timeframe = translate(component.translations!, 'timeframe');
            const redirectUrl = component?.metadata?.redirect_url;
            return (
              <div
                className={classNames(styles.timelineEntry, {
                  [styles.lastEntry]: index === content.components.length - 1,
                })}
                key={component.id}
              >
                <div className={styles.timelineProgress}>
                  {progress === Progress.CURRENT && <ProgressCurrent />}
                  {progress === Progress.FUTURE && <ProgressFuture />}
                  {progress === Progress.COMPLETE && <ProgressComplete />}
                </div>
                <div className={styles.timelineEntries}>
                  <div className={styles.timelineEntryTitle}>
                    <h3 className="h5">
                      {redirectUrl ? (
                        <a
                          href={redirectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.timelineEntryTitleLink}
                        >
                          {entryTitle} <Icon height={16} width={16} name="external-link" />
                        </a>
                      ) : (
                        entryTitle
                      )}
                    </h3>
                  </div>
                  <div className={styles.timelineEntryDates}>
                    {timeframe && <div className={styles.timelineEntryTimeframe}>{timeframe}</div>}
                  </div>
                  <div className={styles.timelineEntryDescription}>{description}</div>
                  <Spacer size="medium" orientation="vertical" />
                </div>
              </div>
            );
          })}
        </div>
        {showByDefault > 0 && phaseCount > showByDefault && (
          <ActionButton
            className={styles.showAllPhasesButton}
            type={ButtonType.ACTION_SECONDARY}
            onClick={() => setShowAllPhases(!showAllPhases)}
          >
            {showAllPhases ? t('timeline.showLess') : t('timeline.showAllPhases')}
            <Icon name={showAllPhases ? 'chevron-up' : 'chevron-down'} />
          </ActionButton>
        )}
      </div>
    </ContentContainer>
  );
}
